/**
 * Ruler Guides Service
 *
 * Manages ruler guide persistence per spread via editor settings API.
 *
 * API endpoints:
 * - GET /checkpoints/{spreadId}/editorsettings/rulerguides         -> Get ruler guides
 * - PUT /checkpoints/{spreadId}/editorsettings/rulerguides/update  -> Update ruler guides
 */

import { httpClient } from '@/api/http/client'
import { logger } from '@/utils/logger'
import type { Guide } from '@/lib/guides/types/guide'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'
import {
  IDENTITY_PROJECTION,
  centeredToDocPx,
  docPxToCentered,
} from '@/lib/canvas/document-projection'

export interface RulerGuideDto {
  uid: number
  x: number
  y: number
}

export interface RulerGuidesDto {
  horizontalGuides?: { guides?: RulerGuideDto[] }
  verticalGuides?: { guides?: RulerGuideDto[] }
}

function enc(value: string): string {
  return encodeURIComponent(value.trim())
}

function editorSettingsPath(spreadId: string): string {
  return `/checkpoints/${enc(spreadId)}/editorsettings/rulerguides`
}

let nextUid = 1

/**
 * `horizontalGuideAnchorX` is legacy's left-pin for a horizontal guide's rendered line (its
 * `<Guide X>`, spine-relative like every other X). Legacy positions + sizes the line from that X
 * with a fixed width covering every page in the spread, so an X other than the true left edge of
 * the spread's leftmost page makes the line start (and therefore end) in the wrong place — it
 * does not merely mis-place a point the way it would for a vertical guide. Unused on our own load
 * path (`dtoToGuides` never reads horizontal `x`) — write-only, for legacy interop.
 */
function guidesToDto(
  guides: Guide[],
  projection: DocumentProjection = IDENTITY_PROJECTION,
  horizontalGuideAnchorX = 0
): RulerGuidesDto {
  const horizontal = guides.filter((g) => g.type === 'horizontal')
  const vertical = guides.filter((g) => g.type === 'vertical')

  return {
    horizontalGuides: {
      guides: horizontal.map((g, i) => ({
        uid: i + 1,
        x: horizontalGuideAnchorX,
        y: docPxToCentered({ x: 0, y: g.position }, projection).y,
      })),
    },
    verticalGuides: {
      guides: vertical.map((g, i) => ({
        uid: i + 1,
        x: docPxToCentered({ x: g.position, y: 0 }, projection).x,
        y: 0,
      })),
    },
  }
}

function safePos(value: unknown): number {
  const n = Number(value)

  return Number.isFinite(n) ? n : 0
}

function dtoToGuides(
  dto: RulerGuidesDto,
  projection: DocumentProjection = IDENTITY_PROJECTION
): Guide[] {
  const guides: Guide[] = []

  const hGuides = dto.horizontalGuides?.guides ?? []

  for (const g of hGuides) {
    guides.push({
      id: `guide-h-${nextUid++}`,
      type: 'horizontal',
      position: centeredToDocPx({ x: 0, y: safePos(g.y) }, projection).y,
    })
  }

  const vGuides = dto.verticalGuides?.guides ?? []

  for (const g of vGuides) {
    guides.push({
      id: `guide-v-${nextUid++}`,
      type: 'vertical',
      position: centeredToDocPx({ x: safePos(g.x), y: 0 }, projection).x,
    })
  }

  return guides
}

async function getRulerGuides(
  spreadId: string,
  projection: DocumentProjection = IDENTITY_PROJECTION
): Promise<Guide[]> {
  try {
    const dto = await httpClient.get<RulerGuidesDto>(editorSettingsPath(spreadId))

    return dtoToGuides(dto, projection)
  } catch (error) {
    logger.warn(`Failed to load ruler guides for spread ${spreadId}:`, error)
    return []
  }
}

async function updateRulerGuides(
  spreadId: string,
  guides: Guide[],
  projection: DocumentProjection = IDENTITY_PROJECTION,
  horizontalGuideAnchorX = 0
): Promise<void> {
  const dto = guidesToDto(guides, projection, horizontalGuideAnchorX)

  await httpClient.put<RulerGuidesDto, unknown>(`${editorSettingsPath(spreadId)}/update`, dto)
}

export const rulerGuidesService = {
  getRulerGuides,
  updateRulerGuides,
  guidesToDto,
  dtoToGuides,
}
